import React, { useRef } from 'react';
import { usePreferences } from '../../contexts/PreferencesContext';
import { audioFilesService } from '../../services/audioFiles';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { AudioWaveLoadingIcon } from '../../icons';
import Button from '../ui/Button';

interface FileUploadAreaProps {
  onFilesSelected: (files: FileList) => void;
  onFileUploaded?: (file: any) => void;
  onUploadError?: (error: string) => void;
  currentFolderId?: string | null;
  isUploading?: boolean;
  acceptedFormats?: string[];
  hasFiles?: boolean;
}

const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  onFilesSelected,
  onFileUploaded,
  onUploadError,
  currentFolderId,
  isUploading = false,
  acceptedFormats = [],
  hasFiles = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { validateFile, serviceConfiguration } = usePreferences();

  // Get max file size from backend configuration
  const maxSizePerFile = serviceConfiguration
    ? Math.round(
        serviceConfiguration.file_constraints.max_file_size / (1024 * 1024)
      )
    : 1000; // Fallback to 1000 MB if configuration is not loaded yet

  const uploadFiles = async (files: FileList) => {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file using service configuration from preferences context
      const validation = validateFile(file);

      if (!validation.isValid) {
        const errorMessage = validation.errors[0] || 'Invalid file';
        onUploadError?.(errorMessage);
        continue;
      }

      try {
        const uploadedFile = await audioFilesService.uploadAudioFile({
          file,
          folderId: currentFolderId || undefined,
        });
        onFileUploaded?.(uploadedFile);
      } catch (error: any) {
        onUploadError?.(error.message || `Failed to upload ${file.name}`);
      }
    }
  };

  const handleDrop = (files: FileList) => {
    onFilesSelected(files);
    uploadFiles(files);
  };

  // Use service configuration directly for more accurate MIME type filtering
  const acceptedTypes =
    serviceConfiguration?.file_constraints.supported_formats || [];

  const handleRejectedFiles = (rejectedFiles: File[]) => {
    const rejectedNames = rejectedFiles.map((f) => f.name).join(', ');
    if (process.env.REACT_APP_TRANSCRIBING_SERVICE_SELECTION === 'true') {
      const serviceName =
        serviceConfiguration?.transcription.active_service_name ||
        'the active transcription service';
      onUploadError?.(
        `File(s) ${rejectedNames} are not supported by ${serviceName}`
      );
    } else {
      onUploadError?.(`File(s) ${rejectedNames} are not supported`);
    }
  };

  const { isDragOver, dragProps } = useDragAndDrop({
    onDrop: handleDrop,
    onRejectedFiles: handleRejectedFiles,
    acceptedTypes,
  });

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onFilesSelected(files);
      uploadFiles(files);
    }
  };

  const handleChooseFiles = () => {
    fileInputRef.current?.click();
  };

  const acceptString = acceptedFormats
    .map((format) => {
      switch (format.toLowerCase()) {
        case 'mp3':
          return '.mp3,audio/mp3,audio/mpeg';
        case 'mp4':
          return '.mp4,audio/mp4';
        case 'wav':
          return '.wav,audio/wav,audio/wave';
        case 'm4a':
          return '.m4a,audio/m4a,audio/mp4';
        case 'flac':
          return '.flac,audio/flac';
        case 'aac':
          return '.aac,audio/aac';
        case 'ogg':
          return '.ogg,audio/ogg';
        case 'wma':
          return '.wma,audio/x-ms-wma';
        default:
          return `.${format.toLowerCase()}`;
      }
    })
    .join(',');

  return (
    <div
      data-tutorial="upload-area"
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ${
        isDragOver
          ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 scale-[1.02]'
          : isUploading
            ? 'border-blue-300 dark:border-blue-600 bg-blue-50/50 dark:bg-blue-900/10'
            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
      } ${isUploading ? 'pointer-events-none' : ''}`}
      {...dragProps}
    >
      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
        {isUploading ? 'Uploading Audio File...' : 'Upload Audio File'}
      </h3>

      {/* Description */}
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        {isUploading ? (
          <span className="flex items-center justify-center gap-2">
            <AudioWaveLoadingIcon size={24} className="text-blue-500" />
          </span>
        ) : (
          'Drag and drop files here, or click to browse'
        )}
      </p>

      {/* File Format Info */}
      {!isUploading && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {serviceConfiguration
            ? process.env.REACT_APP_TRANSCRIBING_SERVICE_SELECTION === 'true'
              ? `Supports formats for ${serviceConfiguration.transcription.active_service_name} • Max ${maxSizePerFile} MB per file`
              : `Supports most common audio/video formats • Max ${maxSizePerFile} MB per file`
            : `Max ${maxSizePerFile} MB per file`}
        </p>
      )}

      {/* Choose Files Button */}
      {!isUploading && (
        <Button
          variant="secondary"
          onClick={handleChooseFiles}
          disabled={isUploading}
        >
          Choose Files
        </Button>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptString}
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
};

export default FileUploadArea;
